import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, ShieldCheck, FileText } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { useContent } from "../lib/useContent";

export default function TimeshareReviews() {
  const { data: timeshares, loading } = useContent("/timeshares");
  return (
    <>
      <SEO
        title="Timeshare Reviews"
        description="Honest, consumer-first reviews of the top 5 timeshares & vacation clubs — Marriott Vacation Club, Hilton Grand Vacations, Disney Vacation Club, Club Wyndham, and Westgate Resorts. Plus a guide to surviving the sales presentation."
        path="/timeshare-reviews"
        keywords={["Timeshare Reviews", "Best Timeshares", "Marriott Vacation Club", "Disney Vacation Club", "Hilton Grand Vacations", "Club Wyndham", "Westgate Resorts"]}
      />

      {/* HERO */}
      <section className="relative flex min-h-[58vh] items-end overflow-hidden">
        <img
          src="https://images.pexels.com/photos/11788440/pexels-photo-11788440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600"
          alt="Elegant high-rise resort towers beside a bright seaside, representing top timeshares"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-white">
              <ShieldCheck className="h-4 w-4" /> Independent & Unbiased
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Timeshare Reviews
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">
              Our honest, consumer-first breakdown of the top 5 timeshares and vacation clubs —
              the real pros, cons, fees, and the bottom line before you ever sign.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PRESENTATION SUBPAGE BANNER */}
      <section className="mx-auto max-w-7xl px-5 pt-16 lg:px-8">
        <Reveal>
          <Link
            to="/timeshare-reviews/presentation-guide"
            data-testid="presentation-guide-link"
            className="group flex flex-col items-start justify-between gap-4 rounded-3xl bg-navy p-8 sm:flex-row sm:items-center"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-white/10 text-accent">
                <FileText className="h-6 w-6" />
              </span>
              <div>
                <h2 className="font-display text-xl font-bold text-white">
                  Invited to a Timeshare Presentation?
                </h2>
                <p className="mt-1 text-sm text-slate-300">
                  Read our survival guide first — what to expect, the high-pressure tactics, and how to protect your assets.
                </p>
              </div>
            </div>
            <span className="inline-flex flex-none items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
              Open the Guide
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </Link>
        </Reveal>
      </section>

      {/* TIMESHARE LIST */}
      <section className="section-pad mx-auto max-w-7xl space-y-10 px-5 lg:px-8">
        <Reveal>
          <Overline>The Top 5</Overline>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
            Ranked, reviewed, and refreshingly honest.
          </h2>
        </Reveal>

        {loading && <PageLoader />}
        {(timeshares || []).map((ts) => (
          <Reveal key={ts.slug} delay={0.04}>
            <article
              data-testid={`timeshare-card-${ts.slug}`}
              className="grid overflow-hidden rounded-[2rem] card-3d lg:grid-cols-2"
            >
              <div className="relative min-h-[280px] overflow-hidden">
                <img src={ts.image} alt={ts.alt} className="h-full w-full object-cover" />
                <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-1.5 text-sm font-bold text-navy backdrop-blur">
                  <Star className="h-4 w-4 fill-accent text-accent" />
                  {ts.rating.toFixed(1)} / 5
                </span>
              </div>
              <div className="flex flex-col p-8 sm:p-10">
                <Overline>{ts.verdict}</Overline>
                <h3 className="mt-2 font-display text-3xl font-bold tracking-tight text-navy">
                  {ts.name}
                </h3>
                <p className="mt-3 leading-relaxed text-slate-600">{ts.summary}</p>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  {ts.scores.map((s) => (
                    <div key={s.label}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-navy">{s.label}</span>
                        <span className="font-semibold text-secondary">{s.value}</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-secondary to-primary"
                          style={{ width: `${s.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <Link
                  to={`/timeshare-reviews/${ts.slug}`}
                  data-testid={`timeshare-readmore-${ts.slug}`}
                  className="group mt-8 inline-flex items-center gap-2 self-start rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy/90"
                >
                  Read the full review
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </section>
    </>
  );
}
